import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  LinearProgress,
} from '@mui/material';
import { employeeService } from '../../services/employeeService';
import { leaveService } from '../../services/leaveService';
import { holidayService } from '../../services/holidayService';
import { projectService } from '../../services/projectService';
import { locationService } from '../../services/locationService';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Card colors for different upload types
const cardColors = {
  employees: { bg: 'linear-gradient(135deg, #5C6BC0 0%, #7986CB 100%)', icon: <PeopleIcon sx={{ fontSize: 40 }} /> },
  leaves: { bg: 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)', icon: <EventBusyIcon sx={{ fontSize: 40 }} /> },
  holidays: { bg: 'linear-gradient(135deg, #E65100 0%, #FF8F00 100%)', icon: <EventIcon sx={{ fontSize: 40 }} /> },
  projects: { bg: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', icon: <FolderIcon sx={{ fontSize: 40 }} /> },
  locations: { bg: 'linear-gradient(135deg, #6A1B9A 0%, #9C27B0 100%)', icon: <LocationOnIcon sx={{ fontSize: 40 }} /> },
};

const BatchUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadType, setUploadType] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [inputKey, setInputKey] = useState(Date.now()); // Key to reset file inputs

  const handleFileChange = (event, type) => {
    setSelectedFile(event.target.files[0]);
    setUploadType(type);
    setError('');
    setSuccess('');
    setUploadResult(null);
  };

  // Parse CSV file to array of records
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must have a header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim());
    const records = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) continue;

      const record = {};
      headers.forEach((header, idx) => {
        record[header] = values[idx] || null;
      });
      records.push(record);
    }

    return records;
  };

  // Parse employee records with proper type conversion
  const parseEmployeeRecords = (records) => {
    return records.map(row => ({
      emp_id: row.emp_id,
      name: row.name,
      location: row.location,
      project_code: row.project_code,
      onboarding_date: row.onboarding_date,
      offboarding_date: row.offboarding_date || null,
      bill_rate: parseFloat(row.bill_rate) || 0,
      discount: parseFloat(row.discount) || 0,
      min_billable_days: row.min_billable_days ? parseInt(row.min_billable_days) : undefined,
      max_billable_days: row.max_billable_days ? parseInt(row.max_billable_days) : undefined,
    }));
  };

  // Parse leave records with proper type conversion
  const parseLeaveRecords = (records) => {
    return records.map(row => ({
      emp_id: row.emp_id,
      project_code: row.project_code,
      month: row.month,
      leave_count: parseInt(row.leave_count) || 0,
    }));
  };

  // Parse holiday records with proper type conversion
  const parseHolidayRecords = (records) => {
    return records.map(row => ({
      location: row.location,
      date: row.date,
      name: row.name,
      description: row.description || null,
    }));
  };

  // Parse project records with proper type conversion
  const parseProjectRecords = (records) => {
    return records.map(row => ({
      project_code: row.project_code,
      name: row.name,
      description: row.description || null,
    }));
  };

  // Parse location records with proper type conversion
  const parseLocationRecords = (records) => {
    return records.map(row => ({
      location_name: row.location_name,
      country: row.country || null,
    }));
  };

  const handleUpload = async (type) => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let result;
      switch (type) {
        case 'employees':
          // Read and parse CSV file
          const text = await selectedFile.text();
          const rawRecords = parseCSV(text);
          const employeeRecords = parseEmployeeRecords(rawRecords);
          result = await employeeService.batchUpload(employeeRecords);
          break;
        case 'leaves':
          // Read and parse CSV file for leaves
          const leaveText = await selectedFile.text();
          const leaveRawRecords = parseCSV(leaveText);
          const leaveRecords = parseLeaveRecords(leaveRawRecords);
          result = await leaveService.batchUpload(leaveRecords);
          break;
        case 'holidays':
          // Read and parse CSV file for holidays
          const holidayText = await selectedFile.text();
          const holidayRawRecords = parseCSV(holidayText);
          const holidayRecords = parseHolidayRecords(holidayRawRecords);
          result = await holidayService.batchUpload(holidayRecords);
          break;
        case 'projects':
          // Read and parse CSV file for projects
          const projectText = await selectedFile.text();
          const projectRawRecords = parseCSV(projectText);
          const projectRecords = parseProjectRecords(projectRawRecords);
          result = await projectService.batchUpload(projectRecords);
          break;
        case 'locations':
          // Read and parse CSV file for locations
          const locationText = await selectedFile.text();
          const locationRawRecords = parseCSV(locationText);
          const locationRecords = parseLocationRecords(locationRawRecords);
          result = await locationService.batchUpload(locationRecords);
          break;
        default:
          throw new Error('Invalid upload type');
      }

      setUploadResult(result);
      let successMsg = `Successfully uploaded ${result.success || result.created || 0} records.`;
      if (result.skipped) {
        successMsg += ` ${result.skipped} skipped (already exist).`;
      }
      if (result.failed) {
        successMsg += ` ${result.failed} failed.`;
      }
      if (result.invalidLocations && result.invalidLocations.length > 0) {
        successMsg += ` Invalid locations: ${result.invalidLocations.join(', ')}`;
      }
      setSuccess(successMsg);
      setSelectedFile(null);
      setInputKey(Date.now()); // Reset file inputs to allow re-selection
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type) => {
    let csvContent = '';
    let filename = '';

    switch (type) {
      case 'employees':
        csvContent = 'emp_id,name,location,project_code,onboarding_date,offboarding_date,bill_rate,discount,min_billable_days,max_billable_days\nE001,John Doe,Bangalore,220530,2025-01-01,2025-12-31,100,0,0,31\n';
        filename = 'employees_template.csv';
        break;
      case 'leaves':
        csvContent = 'emp_id,project_code,month,leave_count\nEMP001,220530,2025-01-01,2\nEMP002,220530,2025-02-01,3\n';
        filename = 'leaves_template.csv';
        break;
      case 'holidays':
        csvContent = 'date,name,location,description\n2025-12-25,Christmas Day,LocationName,Optional description\n2025-01-01,New Year Day,LocationName,\n';
        filename = 'holidays_template.csv';
        break;
      case 'projects':
        csvContent = 'project_code,name\nPROJ-001,Mobile App Development\nPROJ-002,Website Redesign\n';
        filename = 'projects_template.csv';
        break;
      case 'locations':
        csvContent = 'location_name,country\nBangalore,India\nChicago,USA\n';
        filename = 'locations_template.csv';
        break;
      default:
        return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const uploadCards = [
    {
      type: 'employees',
      title: 'Upload Employees',
      description: 'Upload new employees with project assignments. Validates location, project, and SOW dates.',
      fields: 'emp_id, name, location, project_code, onboarding_date, offboarding_date, bill_rate, discount, min_billable_days, max_billable_days',
    },
    {
      type: 'leaves',
      title: 'Upload Leaves',
      description: 'Batch upload monthly leave counts. Creates or updates leave records.',
      fields: 'emp_id, project_code, month (YYYY-MM-01), leave_count',
    },
    {
      type: 'holidays',
      title: 'Upload Holidays',
      description: 'Batch upload holiday calendar. Location must exist in the system.',
      fields: 'Required: date (YYYY-MM-DD), name, location | Optional: description',
    },
    {
      type: 'projects',
      title: 'Upload Projects',
      description: 'Batch upload project records from CSV or Excel file',
      fields: 'project_code, name',
    },
    {
      type: 'locations',
      title: 'Upload Locations',
      description: 'Batch upload location records. Skips existing location names.',
      fields: 'Required: location_name | Optional: country',
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 2, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #FF6F00 0%, #FFA726 100%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(255, 111, 0, 0.3)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudUploadIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Batch Upload</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Upload multiple records at once using CSV or Excel files
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {uploadResult && uploadResult.errors && uploadResult.errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Upload completed with errors:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {uploadResult.errors.slice(0, 5).map((err, idx) => (
                <li key={idx}>{typeof err === 'string' ? err : err.error}</li>
              ))}
              {uploadResult.errors.length > 5 && (
                <li>...and {uploadResult.errors.length - 5} more errors</li>
              )}
            </ul>
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Grid container spacing={3}>
          {uploadCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.type}>
              <Card sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                },
              }}>
                {/* Colorful Card Header */}
                <Box sx={{
                  background: cardColors[card.type]?.bg || 'linear-gradient(135deg, #5C6BC0 0%, #7C4DFF 100%)',
                  color: 'white',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}>
                  {cardColors[card.type]?.icon}
                  <Typography variant="h6" fontWeight="bold">
                    {card.title}
                  </Typography>
                </Box>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {card.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="textSecondary">
                    <strong>Fields:</strong> {card.fields}
                  </Typography>
                </CardContent>
                <CardActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadTemplate(card.type)}
                  >
                    Download Template
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    Select File
                    <input
                      key={inputKey}
                      type="file"
                      hidden
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => handleFileChange(e, card.type)}
                    />
                  </Button>
                  {selectedFile && uploadType === card.type && (
                    <>
                      <Typography variant="caption" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>
                        Selected: {selectedFile.name}
                      </Typography>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleUpload(card.type)}
                        disabled={loading}
                      >
                        Upload Now
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default BatchUpload;
